import { useEffect, useMemo, useState } from "react";
import type { Classroom, Instructor } from "@models/ISchema";
import type { SessionAssignationResourceTarget } from "../../types/session-assignation";

export const useSessionResourceDraft = (
  target: SessionAssignationResourceTarget | null,
  onDirty: (dirty: boolean) => void,
  isOpen = true,
) => {
  const initialDraft = useMemo(
    () => ({
      target,
      selected: [...(target ? target[target.field] : [])] as (Classroom | Instructor)[],
      shouldFilter: true,
      issue: null as string | null,
    }),
    [target],
  );
  const [draft, setDraft] = useState(initialDraft);
  const current = draft.target === target ? draft : initialDraft;
  const { selected, shouldFilter, issue } = current;
  useEffect(() => setDraft(initialDraft), [initialDraft]);
  const update = (
    patch:
      | Partial<typeof initialDraft>
      | ((value: typeof initialDraft) => Partial<typeof initialDraft>),
  ) =>
    setDraft(previous => {
      const value = previous.target === target ? previous : initialDraft;
      return { ...value, ...(typeof patch === "function" ? patch(value) : patch) };
    });
  const idsKey = (items: readonly (Classroom | Instructor)[]) =>
    JSON.stringify(items.map(item => item.id).sort());
  const dirty = !!(isOpen && target && idsKey(selected) !== idsKey(target[target.field]));
  useEffect(() => {
    onDirty(dirty);
  }, [dirty, onDirty]);
  return {
    selected,
    shouldFilter,
    issue,
    setIssue: (issue: string | null) => update({ issue }),
    setShouldFilter: (shouldFilter: boolean) => update({ shouldFilter }),
    select: (item: Classroom | Instructor) =>
      update(value => ({
        selected: value.selected.some(resource => resource.id === item.id)
          ? value.selected
          : [...value.selected, item],
        issue: null,
      })),
    remove: (item: Classroom | Instructor) =>
      update(value => ({
        selected: value.selected.filter(resource => resource.id !== item.id),
        issue: null,
      })),
  };
};
